// Spot the Fraud: compare a verified original with a version an attacker
// changed in exactly one place, click the changed detail, then inspect.
// Five rounds (email, invoice, attachment, shared document, website).
// All rules and round data live in logic.ts; this file is only the cabinet.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { CSSProperties, ReactNode } from "react";
import { useSim } from "../../state/store";
import { audio } from "../../lib/audio";
import type { ScreenBox, SpotFraudConfig } from "../../scenario/types";
import { ArcadeBezel, CrtOverlay, ExitButton, bigRects, cabinetRect, padScore, useEscapeExit, useViewport } from "../arcade/Kit";
import { exitArcadeDevice, makeResult, saveResult } from "../arcade/results";
import { FrogWarning, useMoveOnLabel } from "../arcade/Outcome";
import { reportCabinet, type DecisionRecord } from "../arcade/report";
import { arcadeSfx } from "../arcade/sfx";
import {
  ROUNDS,
  SCORE,
  addPoints,
  buildResultInput,
  diffParts,
  fieldDef,
  judge,
  manipulate,
  outcomePoints,
  roundPoints,
  type FieldDef,
  type Outcome,
  type RoundDef,
  type RoundRecord,
} from "./logic";
import "./spotfraud.css";
import { tx, txr } from "../../lib/tx";

type Phase = "attract" | "credit" | "expanding" | "intro" | "play" | "end" | "collapsing";
type Side = "orig" | "fake";

interface Reveal {
  outcome: Outcome;
  points: number;
}

const ANIM_MS = 720;

const DECISION_RESULT: Record<Outcome, DecisionRecord["result"]> = {
  found: "correct",
  wrong: "incorrect",
  skipped: "skipped",
};

const APP_LABEL: Record<RoundDef["type"], string> = {
  email: "Mail",
  invoice: "Invoice.pdf",
  attachment: "Mail \u00b7 1 attachment",
  shareddoc: "Shared with you",
  website: "Browser",
};

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(
    () => typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    if (typeof window.matchMedia !== "function") return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setReduced(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return reduced;
}

function useArcadeSound(): [boolean, () => void] {
  const [on, setOn] = useState(() => arcadeSfx.isEnabled());
  useEffect(() => arcadeSfx.subscribe(() => setOn(arcadeSfx.isEnabled())), []);
  const toggle = useCallback(() => {
    const next = !arcadeSfx.isEnabled();
    arcadeSfx.setEnabled(next);
    if (next) {
      arcadeSfx.unlock();
      arcadeSfx.select();
    }
  }, []);
  return [on, toggle];
}

// ------------------------------------------------------------------ icons

function Invader({ lit }: { lit: boolean }) {
  return (
    <svg className={`sf-inv${lit ? " is-lit" : ""}`} viewBox="0 0 11 8" aria-hidden="true" shapeRendering="crispEdges">
      <path d="M2 0h1v1H2zM8 0h1v1H8zM3 1h5v1H3zM2 2h7v1H2zM1 3h2v1H1zM4 3h3v1H4zM8 3h2v1H8zM0 4h11v1H0zM0 5h1v1H0zM2 5h7v1H2zM10 5h1v1h-1zM0 6h1v1H0zM2 6h1v1H2zM8 6h1v1H8zM10 6h1v1h-1zM3 7h2v1H3zM6 7h2v1H6z" />
    </svg>
  );
}

function Trophy() {
  return (
    <svg className="sf-trophy" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 3h10v2h3v3a4 4 0 0 1-4 4h-.3A5 5 0 0 1 13 14.9V17h3v2H8v-2h3v-2.1A5 5 0 0 1 8.3 12H8a4 4 0 0 1-4-4V5h3V3zm0 4H6v1a2 2 0 0 0 1 1.7V7zm10 0v2.7A2 2 0 0 0 18 8V7h-1zM6 20h12v2H6z" />
    </svg>
  );
}

function ShieldCheck() {
  return (
    <svg className="sf-hicon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 2l8 3v6c0 5-3.4 9.4-8 11-4.6-1.6-8-6-8-11V5l8-3z" fill="none" stroke="currentColor" strokeWidth="2" />
      <path d="M8 12l3 3 5-6" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function WarnIcon() {
  return (
    <svg className="sf-hicon" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3l10 18H2L12 3z" fill="none" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
      <path d="M12 10v5M12 17.5v.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  );
}

function FileIcon({ exe }: { exe?: boolean }) {
  return (
    <svg className="sf-fileicon" viewBox="0 0 20 24" aria-hidden="true">
      <path d="M2 1h11l5 5v17H2z" fill="#eef2f7" stroke="#8a97a8" strokeWidth="1.2" />
      <path d="M13 1v5h5" fill="none" stroke="#8a97a8" strokeWidth="1.2" />
      <rect x="4" y="13" width="12" height="6" rx="1" fill={exe ? "#6b7686" : "#c8452f"} />
    </svg>
  );
}

function SpeakerIcon({ on }: { on: boolean }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="sf-spk">
      <path d="M4 9h4l5-4v14l-5-4H4z" fill="currentColor" />
      {on ? (
        <path d="M16 8.5a5 5 0 0 1 0 7M18.5 6a8.5 8.5 0 0 1 0 12" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      ) : (
        <path d="M16 9l5 6M21 9l-5 6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      )}
    </svg>
  );
}

// ------------------------------------------------------------------ text with a diff mark

function Marked({ value, other, mark }: { value: string; other: string; mark: boolean }) {
  if (!mark || value === other) return <>{value}</>;
  const d = diffParts(value, other);
  return (
    <>
      {d.prefix}
      <mark className="sf-mark">{d.a || "\u2009"}</mark>
      {d.suffix}
    </>
  );
}

// ------------------------------------------------------------------ document card

interface DocProps {
  round: RoundDef;
  side: Side;
  values: Record<string, string>;
  other: Record<string, string>;
  picked: string | null;
  reveal: Reveal | null;
  hintOn: boolean;
  hover: string | null;
  onHover: (key: string | null) => void;
  onPick?: (key: string) => void;
  /** Hidden details are shown (INSPECT pressed or the case answered). */
  inspected: boolean;
}

/** File name as a mail app shows it by default: the last extension is hidden. */
function shortFileName(name: string): string {
  const i = name.lastIndexOf(".");
  return i > 0 ? name.slice(0, i) : name;
}

function fieldContent(f: FieldDef, value: string, other: string, markDiff: boolean, inspected: boolean): ReactNode {
  const text = <Marked value={value} other={other} mark={markDiff} />;
  switch (f.kind) {
    case "link":
      return (
        <span className="sf-linkwrap">
          <span className="sf-linktext">{f.text ?? value}</span>
          {inspected && (
            <span className="sf-inspected">
              <span className="sf-inspected__k">{tx("LINK GOES TO")}</span> <Marked value={value} other={other} mark={markDiff} />
            </span>
          )}
        </span>
      );
    case "button":
      return <span className="sf-fakebtn">{text}</span>;
    case "file":
      return (
        <span className="sf-file">
          <FileIcon exe={inspected && /\.exe$/i.test(value)} />
          <span>{inspected ? text : shortFileName(value)}</span>
        </span>
      );
    default:
      return text;
  }
}

function DocCard({ round, side, values, other, picked, reveal, hintOn, hover, onHover, onPick, inspected }: DocProps) {
  const changed = round.mutation.field;
  const isFake = side === "fake";
  const linkField = round.sections.flatMap((s) => s.fields).find((f) => f.kind === "link");
  const statusKey = hover ?? (linkField && isFake && picked === linkField.key ? picked : null) ?? (reveal && linkField ? linkField.key : null);
  const status = statusKey && linkField && statusKey === linkField.key ? values[linkField.key] : null;

  return (
    <div className={`sf-card sf-card--${round.type}`}>
      <div className="sf-card-bar">
        <span className="sf-dots" aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
        <span>{APP_LABEL[round.type]}</span>
      </div>
      <div className={`sf-card-body sf-body--${round.type}`}>
        {round.sections.map((sec) => {
          const hinted = hintOn && isFake && !reveal && sec.id === round.hintSection;
          return (
            <div key={sec.id} className={`sf-sec sf-sec--${sec.id}${hinted ? " is-hint" : ""}`}>
              {hinted && <span className="sf-hinttag">{tx("HINT: LOOK HERE")}</span>}
              {sec.fields.map((f) => {
                const value = values[f.key] ?? "";
                if (f.hidden && !inspected) {
                  return (
                    <div key={f.key} className="sf-f sf-f--hidden">
                      {txr("{label}: hidden, press INSPECT", { label: f.label })}
                    </div>
                  );
                }
                const markDiff = !!reveal && f.key === changed;
                const cls = ["sf-f", `sf-f--${f.kind ?? "text"}`];
                let tag: string | null = null;
                if (isFake) {
                  if (!reveal && picked === f.key) cls.push("is-picked");
                  if (reveal) {
                    if (f.key === changed) {
                      cls.push(reveal.outcome === "found" ? "is-found" : "is-changed");
                      tag = reveal.outcome === "found" ? "FOUND" : "CHANGED HERE";
                    } else if (picked === f.key) {
                      cls.push("is-wrongpick");
                      tag = "YOUR PICK";
                    }
                  }
                } else if (reveal && f.key === changed) {
                  cls.push("is-orig");
                }
                const content = (
                  <>
                    {fieldContent(f, value, other[f.key] ?? "", markDiff, inspected)}
                    {tag && <span className="sf-ftag">{tag}</span>}
                  </>
                );
                const hoverProps =
                  f.kind === "link"
                    ? { onMouseEnter: () => onHover(f.key), onMouseLeave: () => onHover(null), onFocus: () => onHover(f.key), onBlur: () => onHover(null) }
                    : {};
                if (isFake && onPick) {
                  return (
                    <button
                      key={f.key}
                      type="button"
                      className={cls.join(" ")}
                      disabled={!!reveal}
                      aria-pressed={picked === f.key}
                      aria-label={`${f.label}: ${f.kind === "link" ? f.text ?? "" : value}`}
                      onClick={() => onPick(f.key)}
                      {...hoverProps}
                    >
                      {content}
                    </button>
                  );
                }
                return (
                  <div key={f.key} className={cls.join(" ")} {...hoverProps}>
                    {content}
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
      {round.type === "website" && (
        <div className={`sf-status${status ? " is-on" : ""}`}>
          {status ? <Marked value={status} other={other[linkField!.key] ?? ""} mark={!!reveal} /> : tx("Hover over a link to see where it really goes")}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ main

export function SpotFraud({ config, screenBox, onDone }: { config: SpotFraudConfig; screenBox: ScreenBox | null; onDone: () => void }) {
  const title = (config.title ?? "SPOT THE FRAUD").toUpperCase();
  const vp = useViewport();
  const reduced = usePrefersReducedMotion();
  const [soundOn, toggleSound] = useArcadeSound();

  const [phase, setPhase] = useState<Phase>("attract");
  const [idx, setIdx] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [hintUsed, setHintUsed] = useState(false);
  const [reveal, setReveal] = useState<Reveal | null>(null);
  const [score, setScore] = useState(0);
  const [records, setRecords] = useState<RoundRecord[]>([]);
  const [hover, setHover] = useState<{ side: Side; key: string } | null>(null);
  /** Hidden details shown (INSPECT pressed, or the case is answered). */
  const [inspected, setInspected] = useState(false);
  const moveOn = useMoveOnLabel();
  const roundStart = useRef(performance.now());
  const saved = useRef(false);

  const timers = useRef<number[]>([]);
  const later = useCallback((fn: () => void, ms: number) => {
    timers.current.push(window.setTimeout(fn, ms));
  }, []);
  useEffect(
    () => () => {
      timers.current.forEach((t) => window.clearTimeout(t));
      timers.current = [];
    },
    [],
  );

  const big = phase !== "attract" && phase !== "credit" && phase !== "collapsing";
  const animating = phase === "expanding" || phase === "collapsing";

  useEffect(() => {
    if (!big) return;
    audio.holdMediaDuck("spotfraud");
    return () => audio.releaseMediaDuck("spotfraud");
  }, [big]);

  const rects = bigRects(vp);
  const cab = cabinetRect(vp, screenBox);
  const glass = big ? rects.glass : cab;
  const glassStyle: CSSProperties = { left: glass.left, top: glass.top, width: glass.width, height: glass.height };

  const round = ROUNDS[idx];
  const fake = useMemo(() => manipulate(round), [round]);
  const orig = round.original as Record<string, string>;

  // ---------------------------------------------------------------- flow
  const resetRun = () => {
    setIdx(0);
    setPicked(null);
    setHintUsed(false);
    setInspected(false);
    setReveal(null);
    setScore(0);
    setRecords([]);
    setHover(null);
    saved.current = false;
  };

  const insertCoin = () => {
    arcadeSfx.unlock();
    arcadeSfx.coin();
    setPhase("credit");
  };

  const pressStart = () => {
    arcadeSfx.unlock();
    arcadeSfx.start();
    resetRun();
    setPhase("expanding");
    later(() => setPhase("intro"), reduced ? 60 : ANIM_MS);
  };

  const beginCases = () => {
    arcadeSfx.select();
    resetRun();
    roundStart.current = performance.now();
    setPhase("play");
  };

  const leave = useCallback(() => {
    if (phase === "attract" || phase === "credit") {
      exitArcadeDevice(onDone, "spotfraud");
      return;
    }
    if (phase === "collapsing") return;
    // A run left before its end still counts (as not passed) when this
    // cabinet has no result yet; a saved result is never replaced by it.
    if (phase === "play" && !saved.current) {
      saved.current = true;
      saveResult(makeResult("spotfraud", { ...buildResultInput(records, score), completed: false }), { finished: false });
    }
    setPhase("collapsing");
    later(() => exitArcadeDevice(onDone, "spotfraud"), reduced ? 60 : ANIM_MS);
  }, [phase, onDone, later, reduced, records, score]);

  useEscapeExit(leave);

  const pick = (key: string) => {
    if (reveal) return;
    arcadeSfx.select();
    setPicked(key);
  };

  const useHint = () => {
    if (hintUsed || reveal) return;
    arcadeSfx.hint();
    setHintUsed(true);
    setScore((s) => addPoints(s, SCORE.HINT));
  };

  const resolve = (choice: string | null) => {
    if (reveal) return;
    const outcome = judge(round, choice);
    const pts = outcomePoints(outcome, hintUsed);
    if (outcome === "found") arcadeSfx.correct();
    else if (outcome === "wrong") arcadeSfx.wrong();
    else arcadeSfx.select();
    setScore((s) => addPoints(s, pts));
    setReveal({ outcome, points: roundPoints(outcome, hintUsed) });
    setRecords((r) => [
      ...r,
      {
        roundId: round.id,
        outcome,
        hintUsed,
        picked: choice,
        points: roundPoints(outcome, hintUsed),
        ms: Math.round(performance.now() - roundStart.current),
      },
    ]);
  };

  // INSPECT shows the hidden details (sender address, full file names, link
  // targets) on both documents. It is free and does not answer the case.
  const inspect = () => {
    if (inspected || reveal) return;
    arcadeSfx.select();
    setInspected(true);
  };
  const submit = () => {
    if (!picked) return;
    resolve(picked);
  };
  const skip = () => {
    setPicked(null);
    resolve(null);
  };

  const finish = (finalRecords: RoundRecord[], finalScore: number) => {
    if (saved.current) return;
    saved.current = true;
    const result = makeResult("spotfraud", buildResultInput(finalRecords, finalScore));
    saveResult(result);
    const byId = new Map(ROUNDS.map((r) => [r.id, r]));
    const decisions: DecisionRecord[] = finalRecords.map((rec) => {
      const r = byId.get(rec.roundId);
      return {
        id: rec.roundId,
        topic: r?.topic ?? rec.roundId,
        result: DECISION_RESULT[rec.outcome],
        points: rec.points,
        responseMs: rec.ms,
        detail: rec.picked ? (r ? fieldDef(r, rec.picked)?.label ?? rec.picked : rec.picked) : rec.hintUsed ? "skipped (hint used)" : "skipped",
      };
    });
    void reportCabinet(useSim.getState().currentBeatId ?? "", "spotfraud", result, decisions);
  };

  const next = () => {
    if (!reveal) return;
    if (idx + 1 >= ROUNDS.length) {
      finish(records, score);
      if (records.filter((r) => r.outcome === "found").length >= 4) arcadeSfx.fanfare();
      else arcadeSfx.gameOver();
      setPhase("end");
      return;
    }
    arcadeSfx.select();
    setIdx(idx + 1);
    setPicked(null);
    setHintUsed(false);
    setInspected(false);
    setReveal(null);
    setHover(null);
    roundStart.current = performance.now();
  };

  const playAgain = () => {
    arcadeSfx.start();
    beginCases();
  };

  // Keyboard: Enter/Space drive the obvious next step. Buttons keep their own activation.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Enter" && e.key !== " ") return;
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === "BUTTON" || t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      let used = true;
      if (phase === "attract") insertCoin();
      else if (phase === "credit") pressStart();
      else if (phase === "intro") beginCases();
      else if (phase === "play" && reveal) next();
      else if (phase === "play" && picked) submit();
      else used = false;
      if (used) {
        e.preventDefault();
        e.stopPropagation();
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  });

  // ---------------------------------------------------------------- render pieces
  const found = records.filter((r) => r.outcome === "found").length;
  const pickedLabel = picked ? fieldDef(round, picked)?.label ?? picked : null;
  const lastRound = idx + 1 >= ROUNDS.length;
  const ctlTop = Math.min(cab.top + cab.height + 14, vp.h - 64);

  const topBar = (
    <div className="sf-top">
      <div className="sf-roundbox">
        <div className="sf-roundlbl">{tx("ROUND")}</div>
        <div className="sf-roundnum">
          {Math.min(idx + 1, ROUNDS.length)}/{ROUNDS.length}
        </div>
        <div className="sf-invs">
          {ROUNDS.map((r, i) => (
            <Invader key={r.id} lit={i <= idx} />
          ))}
        </div>
      </div>
      <div className="sf-marquee">
        <div className="sf-mtitle">{title}</div>
        <div className="sf-msub">{tx("FIND THE ATTACKER'S CHANGE")}</div>
      </div>
      <div className="sf-right">
        <div className="sf-scorebox">
          <Trophy />
          <div>
            <div className="sf-scorelbl">{tx("SCORE")}</div>
            <div className="sf-scorenum">{padScore(score, 5)}</div>
          </div>
        </div>
        {phase === "play" && (
          <button
            type="button"
            className={`sf-hint${hintUsed ? " is-used" : ""}`}
            onClick={useHint}
            disabled={hintUsed || !!reveal}
            title={hintUsed ? tx("Hint already used this round") : tx("Frame the area that changed (costs 20 points)")}
          >
            <span className="sf-hint-q" aria-hidden="true">?</span>
            <span>
              {txr("HINT<0>{v}</0>", { v: hintUsed ? tx("USED") : tx("{HINT} PTS", { HINT: SCORE.HINT }) }, [<small />])}
            </span>
          </button>
        )}
        <button type="button" className="sf-sound" onClick={toggleSound} aria-pressed={soundOn} title={soundOn ? tx("Sound on") : tx("Sound off")}>
          <SpeakerIcon on={soundOn} />
        </button>
      </div>
    </div>
  );

  const board = (
    <>
      <div className="sf-instr" role="status">
        {reveal ? (
          <span>{lastRound ? tx("Last case done. Press CASE CLOSED for your result.") : tx("Read the lesson, then press NEXT CASE.")}</span>
        ) : pickedLabel ? (
          <span>
            {txr("Selected: <0>{pickedLabel}</0>. Press SUBMIT to answer, or pick another detail.", { pickedLabel }, [<b />])}
          </span>
        ) : (
          <span>{round.instruction}</span>
        )}
      </div>
      <div className="sf-board">
        <section className="sf-panel sf-panel--orig" aria-label={tx("Verified original")}>
          <header className="sf-phead">
            <ShieldCheck />
            {tx("VERIFIED ORIGINAL")}
          </header>
          <DocCard
            round={round}
            side="orig"
            values={orig}
            other={fake}
            picked={null}
            reveal={reveal}
            hintOn={false}
            hover={hover?.side === "orig" ? hover.key : null}
            onHover={(k) => setHover(k ? { side: "orig", key: k } : null)}
            inspected={inspected || !!reveal}
          />
        </section>
        <div className="sf-vs" aria-hidden="true">
          {tx("VS")}
        </div>
        <section className="sf-panel sf-panel--fake" aria-label={tx("Manipulated version")}>
          <header className="sf-phead">
            <WarnIcon />
            {tx("MANIPULATED VERSION")}
          </header>
          <DocCard
            round={round}
            side="fake"
            values={fake}
            other={orig}
            picked={picked}
            reveal={reveal}
            hintOn={hintUsed}
            hover={hover?.side === "fake" ? hover.key : null}
            onHover={(k) => setHover(k ? { side: "fake", key: k } : null)}
            onPick={pick}
            inspected={inspected || !!reveal}
          />
        </section>
      </div>
    </>
  );

  const bottom = reveal ? (
    <div className={`sf-bottom sf-reveal is-${reveal.outcome}`}>
      <div className="sf-verdict">
        <div className="sf-vword">{reveal.outcome === "found" ? tx("CORRECT") : reveal.outcome === "wrong" ? tx("WRONG") : tx("SKIPPED")}</div>
        <div className="sf-vpts">
          {txr("{v}{points} PTS", { v: reveal.points > 0 ? "+" : "", points: reveal.points })}
        </div>
        {reveal.outcome === "found" && !hintUsed && <div className="sf-vbonus">+{txr("{NO_HINT_BONUS} NO-HINT BONUS", { NO_HINT_BONUS: SCORE.NO_HINT_BONUS })}</div>}
      </div>
      <div className="sf-compare">
        <div className="sf-cmp-title">{round.changeLabel.toUpperCase()}</div>
        <div className="sf-cmp-row">
          <span className="sf-cmp-k">{tx("ORIGINAL")}</span>
          <span className="sf-cmp-v is-orig">
            <Marked value={orig[round.mutation.field]} other={fake[round.mutation.field]} mark />
          </span>
        </div>
        <div className="sf-cmp-row">
          <span className="sf-cmp-k">{tx("CHANGED")}</span>
          <span className="sf-cmp-v is-fake">
            <Marked value={fake[round.mutation.field]} other={orig[round.mutation.field]} mark />
          </span>
        </div>
        <div className="sf-lesson">{round.lesson}</div>
      </div>
      <button type="button" className="sf-btn sf-btn--next" onClick={next} autoFocus>
        {lastRound ? tx("CASE CLOSED") : tx("NEXT CASE")}
        <span aria-hidden="true">&#x25B6;</span>
      </button>
    </div>
  ) : (
    <div className="sf-bottom">
      <div className="sf-tabs" role="list" aria-label={tx("Case types")}>
        {ROUNDS.map((r, i) => (
          <div key={r.id} role="listitem" className={`sf-tab${i === idx ? " is-on" : ""}${i < idx ? " is-done" : ""}`}>
            {r.tab}
          </div>
        ))}
      </div>
      <div className="sf-actions">
        <button
          type="button"
          className={`sf-btn sf-btn--look${inspected ? " is-done" : ""}`}
          onClick={inspect}
          disabled={inspected}
          title={inspected ? tx("Hidden details are shown on both documents") : tx("Show hidden details: sender address, full file names, link targets (free)")}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true" className="sf-btnicon">
            <circle cx="10" cy="10" r="6" fill="none" stroke="currentColor" strokeWidth="2.6" />
            <path d="M14.5 14.5L21 21" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" />
          </svg>
          {inspected ? tx("INSPECTED") : tx("INSPECT")}
        </button>
        <button type="button" className="sf-btn sf-btn--inspect" onClick={submit} disabled={!picked} title={picked ? tx("Submit your pick") : tx("Click a detail on the manipulated version first")}>
          {tx("SUBMIT")}
        </button>
        <button type="button" className="sf-btn sf-btn--skip" onClick={skip} title={tx("Skip this case (0 points)")}>
          {tx("SKIP")}
          <span aria-hidden="true">&#x25B6;&#x25B6;</span>
        </button>
      </div>
    </div>
  );

  const intro = (
    <div className="sf-overlay">
      <div className="sf-dialog">
        <div className="sf-dtitle">{title}</div>
        <div className="sf-dsub">{tx("FIVE CASES. ONE CHANGE IN EACH.")}</div>
        <ol className="sf-rules">
          <li>{tx("Compare the verified original with the manipulated version.")}</li>
          <li>{tx("Some details are hidden, as in a real mail app. Press INSPECT to show them (free).")}</li>
          <li>{tx("Click the one detail the attacker changed, then press SUBMIT.")}</li>
          <li>{tx("You get one try per case. A hint frames the area, not the detail.")}</li>
        </ol>
        <div className="sf-pts">
          <span>
            {txr("FOUND <0>+{CORRECT}</0>", { CORRECT: SCORE.CORRECT }, [<b />])}
          </span>
          <span>
            {txr("NO HINT <0>+{NO_HINT_BONUS}</0>", { NO_HINT_BONUS: SCORE.NO_HINT_BONUS }, [<b />])}
          </span>
          <span>
            {txr("WRONG <0>{WRONG}</0>", { WRONG: SCORE.WRONG }, [<b />])}
          </span>
          <span>
            {txr("HINT <0>{HINT}</0>", { HINT: SCORE.HINT }, [<b />])}
          </span>
          <span>
            {tx("SKIP")} <b>0</b>
          </span>
        </div>
        <div className="sf-dactions">
          <button type="button" className="sf-btn sf-btn--inspect" onClick={beginCases} autoFocus>
            {tx("OPEN CASE 1")}
          </button>
        </div>
      </div>
    </div>
  );

  const passedRun = found >= 4;
  const endScreen = (
    <div className="sf-overlay">
      <div className="sf-dialog sf-dialog--end">
        <div className="sf-dtitle">{tx("FRAUD DETECTIVE")}</div>
        <div className="sf-dsub sf-closed">{tx("CASE CLOSED")}</div>
        <div className="sf-endstats">
          <div>
            <span>{tx("SCORE")}</span>
            <b className="sf-endscore">{padScore(score, 5)}</b>
          </div>
          <div>
            <span>{tx("FOUND")}</span>
            <b>
              {found}/{ROUNDS.length}
            </b>
          </div>
        </div>
        <div className={`sf-endline${passedRun ? " is-pass" : " is-fail"}`}>
          {passedRun ? tx("Cabinet cleared: you found at least 4 of 5 changes.") : tx("Not cleared yet: find at least 4 of 5 changes to clear this cabinet.")}
        </div>
        <ul className="sf-recap">
          {records.map((rec) => {
            const r = ROUNDS.find((x) => x.id === rec.roundId)!;
            return (
              <li key={rec.roundId} className={`is-${rec.outcome}`}>
                <span className="sf-rc-mark" aria-hidden="true">
                  {rec.outcome === "found" ? "\u2713" : rec.outcome === "wrong" ? "\u2717" : "\u2013"}
                </span>
                <span className="sf-rc-tab">{r.tab}</span>
                <span className="sf-rc-what">
                  {r.changeLabel}
                  {rec.outcome === "found" ? tx(" found") : rec.outcome === "wrong" ? tx(" missed") : tx(" skipped")}
                  {rec.hintUsed ? tx(" (hint)") : ""}
                </span>
                <span className="sf-rc-pts">
                  {rec.points > 0 ? "+" : ""}
                  {rec.points}
                </span>
              </li>
            );
          })}
        </ul>
        {!passedRun && <FrogWarning />}
        <div className="sf-dactions">
          <button type="button" className="sf-btn sf-btn--inspect" onClick={onDoneLeave(leave)} autoFocus={passedRun}>
            {moveOn}
          </button>
          <button type="button" className="sf-btn sf-btn--skip" onClick={playAgain} autoFocus={!passedRun}>
            {passedRun ? tx("PLAY AGAIN") : tx("TRY AGAIN")}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`arc-root sf-root sf-${phase}${reduced ? " sf-reduced" : ""}`}>
      <div className={`arc-scrim${big ? " is-on" : ""}`} />
      <ArcadeBezel frame={rects.frame} side={rects.side} visible={big && !animating} />
      <div className={`arc-glass sf-glass${big ? " is-big" : " is-cab"}${animating ? " sf-anim" : ""}`} style={glassStyle}>
        {(phase === "attract" || phase === "credit" || phase === "expanding" || phase === "collapsing") && (
          <div className="sf-attract">
            <div className="sf-alogo">
              <span>{tx("SPOT THE")}</span>
              <span>{tx("FRAUD")}</span>
            </div>
            <div className="sf-asub">{tx("FIND THE ATTACKER'S CHANGE")}</div>
            <div className="sf-adocs" aria-hidden="true">
              <span className="sf-adoc is-a" />
              <b>{tx("VS")}</b>
              <span className="sf-adoc is-b" />
            </div>
            {phase === "attract" && <div className="sf-ablink">{tx("INSERT COIN")}</div>}
            {phase === "credit" && (
              <>
                <div className="sf-acredit">{tx("CREDIT 1")}</div>
                <div className="sf-ablink">{tx("PRESS START")}</div>
              </>
            )}
            {phase === "collapsing" && <div className="sf-acredit">{tx("THANKS FOR PLAYING")}</div>}
          </div>
        )}
        {(phase === "intro" || phase === "play" || phase === "end") && (
          <div className="sf-game">
            {topBar}
            {board}
            {bottom}
            {phase === "intro" && intro}
            {phase === "end" && endScreen}
          </div>
        )}
        <CrtOverlay />
      </div>
      {(phase === "attract" || phase === "credit") && (
        <div className="sf-cabctl" style={{ top: ctlTop }}>
          {phase === "attract" ? (
            <button type="button" className="sf-cabbtn is-primary" onClick={insertCoin}>
              {tx("Insert coin")}
            </button>
          ) : (
            <button type="button" className="sf-cabbtn is-primary" onClick={pressStart}>
              {tx("Press start")}
            </button>
          )}
          <button type="button" className="sf-cabbtn" onClick={leave}>
            {tx("Leave cabinet")}
          </button>
        </div>
      )}
      <ExitButton onExit={leave} />
    </div>
  );
}

function onDoneLeave(leave: () => void) {
  return () => {
    arcadeSfx.select();
    leave();
  };
}
